import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import useGetCountries from '../hooks/Magasins/useGetCountries';
import useGetUrlTypes from '../hooks/Magasins/useGetUrlTypes';
import useGetWayTypes from '../hooks/Magasins/useGetWayTypes';

export interface Country {
    id: string | number;
    name: string;
    code?: string;
}

export interface WayType {
    id: string | number;
    name: string;
    code?: string;
}

export interface StoreUrlType {
    id: string | number;
    name: string;
    code?: string;
}

interface StoreDataContextType {
    countries: Country[];
    wayTypes: WayType[];
    urlTypes: StoreUrlType[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

const StoreDataContext = createContext<StoreDataContextType | undefined>(undefined);

export const StoreDataProvider = ({ children }: { children: ReactNode }) => {
    const { data: countries, loading: loadingCountries, error: errorCountries } = useGetCountries();
    const { data: wayTypes, loading: loadingWayTypes, error: errorWayTypes } = useGetWayTypes();
    const { data: urlTypes, loading: loadingUrlTypes, error: errorUrlTypes } = useGetUrlTypes();

    const loading = loadingCountries || loadingWayTypes || loadingUrlTypes;
    const error = errorCountries || errorWayTypes || errorUrlTypes || null;

    const refetch = useCallback(() => {
        window.location.reload();
    }, []);

    const value: StoreDataContextType = useMemo(() => ({
        countries: countries || [],
        wayTypes: wayTypes || [],
        urlTypes: urlTypes || [],
        loading,
        error,
        refetch,
    }), [countries, wayTypes, urlTypes, loading, error, refetch]);

    return <StoreDataContext.Provider value={value}>{children}</StoreDataContext.Provider>;
};

export const useStoreData = (): StoreDataContextType => {
    const context = useContext(StoreDataContext);
    if (context === undefined) {
        throw new Error('useStoreData must be used within a StoreDataProvider');
    }
    return context;
};
